import { ArcRotateCamera, Vector3, Animation } from 'babylonjs'
import { Context3D } from '../3d-context'

/** Options for the 3D camera. */
interface ICameraOptions {
  /** Speed defines the speed of zoom of the camera. */
  speed?: number
  panningSensibility?: number
  lowerRadiusLimit?: number
  upperRadiusLimit?: number
  useAutoRotationBehavior?: boolean
  useBouncingBehavior?: boolean
  angularSensibilityX?: number
  angularSensibilityY?: number
}

/** Options for position, target of camera, use for animation camera. */
interface IAnimatationSet {
  /** frame defines the animation frame speed (1-100). || 100 is slow animate */
  frame: number
  /** value defines the XYZ coordinate */
  value: Vector3
}

/**
 * Creates an arc camera in the scene.
 */
export function setupCamera(
  options: ICameraOptions,
  context = Context3D.get()
) {
  if (!context.scene || !context.canvas3D) return

  // Use Arc camera (same with orbit control)
  const camera = new ArcRotateCamera(
    'main_camera',
    0,
    0,
    0,
    Vector3.Zero(),
    context.scene
  )

  camera.fov = 1.2

  // Attach the camera to the canvas
  camera.attachControl(context.canvas3D, true)

  // Target the camera to scene origin
  camera.setTarget(Vector3.Zero())

  // TODO: Add more props for flexible configuration
  // Config camera parameter
  // Make camera far over it good for mouse zoom, so by set this need to set model big scale too
  // By default model world scale we set at 50 then need to set Z direction further by 80
  camera.setPosition(new Vector3(0, 50, 80))
  // NOTE: camera position on z direction it revert from THREEJS
  camera.speed = options?.speed || 1 // zoom speed // This not work well if object too small or camera not far
  camera.panningSensibility = options?.panningSensibility || 70 // panning speed // more number more slow
  camera.lowerRadiusLimit = options?.lowerRadiusLimit || 30 // zoom in limit base on z direction
  camera.upperRadiusLimit = options?.upperRadiusLimit || 150 // zoom out limit base on z direction
  camera.useAutoRotationBehavior = options?.useAutoRotationBehavior || false // use auto rotate
  camera.useBouncingBehavior = options?.useBouncingBehavior || true // Smart hold rotate when using mouse event
  // camera.angularSensibilityX = options?.angularSensibilityX || 10 // Rotate Speed X With Mouse
  // camera.angularSensibilityY = options?.angularSensibilityY || 5 // Rotate Speed Y With Mouse

  setupAnimationCamera(camera)

  camera.storeState() // Store current setting of camera

  Context3D.set({ camera: camera })

  return camera
}

/**
 * Config camera props for end-user
 */
export function configCamera(
  options: ICameraOptions,
  context = Context3D.get()
) {
  if (!context.camera) return
}

export function toggleAutoRotate(context = Context3D.get()) {
  if (context.camera instanceof ArcRotateCamera) {
    if (!context.camera) return
    context.camera.useAutoRotationBehavior =
      !context.camera.useAutoRotationBehavior
    if (context.camera.autoRotationBehavior) {
      context.camera.autoRotationBehavior.idleRotationSpeed = 0.2
      // context.camera.autoRotationBehavior.idleRotationWaitTime = 3000
    }
  }
}

/**
 * Create & prepare animation camera props for position and target
 */
export function setupAnimationCamera(camera: ArcRotateCamera) {
  const animationCameraPosition = new Animation(
    'animationCameraPosition',
    'position',
    100,
    Animation.ANIMATIONTYPE_VECTOR3,
    Animation.ANIMATIONLOOPMODE_CONSTANT
  )

  const animationCameraTarget = new Animation(
    'animationCameraTarget',
    'target',
    30,
    Animation.ANIMATIONTYPE_VECTOR3,
    Animation.ANIMATIONLOOPMODE_CONSTANT
  )

  camera.animations = [animationCameraPosition, animationCameraTarget]
}

/**
 * Run animation camera to position and target by given value set
 */
function runAnimationCamera(
  positions: IAnimatationSet[],
  targets: IAnimatationSet[],
  context = Context3D.get()
) {
  console.log(
    'context',
    context,
    context.scene,
    context.camera,
    positions,
    targets
  )

  if (!context.scene) return

  const camera = context.scene.getCameraByID('main_camera') as ArcRotateCamera
  if (!camera) return

  const animationCameraPosition = camera.getAnimationByName(
    'animationCameraPosition'
  )
  const animationCameraTarget = camera.getAnimationByName(
    'animationCameraTarget'
  )

  if (!animationCameraPosition || !animationCameraTarget) return

  // Add frame 0 for current position, target
  const currentLowerRadiusLimit = camera.lowerRadiusLimit
  camera.lowerRadiusLimit = null
  const currentPosition = camera.position.clone()
  const currentTarget = camera.target.clone()
  positions.unshift({ frame: 0, value: currentPosition })
  targets.unshift({ frame: 0, value: currentTarget })

  animationCameraPosition.setKeys(positions)
  animationCameraTarget.setKeys(targets)

  context.scene.beginAnimation(camera, 0, 100, false, 1, () => {
    // Callback for EMPTY ANIMATE
    animationCameraPosition.setKeys([])
    animationCameraTarget.setKeys([])
    camera.lowerRadiusLimit = currentLowerRadiusLimit
  })
}

/**
 * Reset camera to initial position, target with animation
 */
export function resetCamera(context = Context3D.get()) {
  // Position & Target you want to animate camera
  // Can add multiple stop point by frame
  // NOTE: Do not add anything for frame 0 because it for current position
  // And leave it empty Array if you dont want to animate position or target

  if (!context.camera) return

  if (context.camera instanceof ArcRotateCamera) {
    setupAnimationCamera(context.camera)
  }

  console.log('context.camera', context.camera)

  const positions = [
    {
      frame: 30,
      // @ts-ignore
      value: context.camera._storedPosition,
    },
  ]

  const targets = [
    {
      frame: 30,
      // @ts-ignore
      value: context.camera._storedTarget,
    },
  ]

  runAnimationCamera(positions, targets)
}
